import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  NOTIFICATION_OUTBOX_STATUSES,
  PERMISSIONS,
  type NotificationOutboxStatus,
} from '@solar/contracts';

import { Alert } from '../../../../components/Alert';
import { Breadcrumbs } from '../../../../components/Breadcrumbs';
import { Button } from '../../../../components/Button';
import { EmptyState } from '../../../../components/EmptyState';
import { SelectField } from '../../../../components/forms/SelectField';
import { PageHeader } from '../../../../components/PageHeader';
import { PageSpinner } from '../../../../components/PageSpinner';
import { StatusBadge } from '../../../../components/StatusBadge';
import { useAuth } from '../../../../features/auth/auth-context';
import { notificationsApi } from '../../../../features/notifications/notifications.api';
import { toDisplayMessage } from '../../../../lib/error-message';
import { paths } from '../../../../routes/paths';
import styles from '../AdminPortal.module.css';

import filterStyles from './NotificationOutboxPage.module.css';

export function NotificationOutboxPage(): React.JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<NotificationOutboxStatus | ''>('');
  const [appliedStatus, setAppliedStatus] = useState<NotificationOutboxStatus | undefined>(
    undefined,
  );

  const outbox = useQuery({
    queryKey: ['admin', 'notifications', 'outbox', { page, status: appliedStatus }],
    queryFn: () =>
      notificationsApi.listOutbox({
        page,
        pageSize: 25,
        status: appliedStatus,
      }),
  });

  const retry = useMutation({
    mutationFn: (id: string) => notificationsApi.retry(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'notifications', 'outbox'] });
    },
  });

  return (
    <div className={styles.page}>
      <PageHeader
        title="Notification outbox"
        subtitle="Monitor notification delivery states. OTP messages cannot be retried here."
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: 'Overview', to: paths.dashboard }, { label: 'Notification outbox' }]}
          />
        }
      />

      <section className={styles.card} aria-labelledby="outbox-filters-heading">
        <h2 className={styles.cardTitle} id="outbox-filters-heading">
          Filter
        </h2>
        <form
          className={filterStyles.filters}
          aria-label="Notification filters"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setAppliedStatus(status === '' ? undefined : status);
          }}
        >
          <SelectField
            label="Status"
            name="status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as NotificationOutboxStatus | '');
            }}
          >
            <option value="">All</option>
            {NOTIFICATION_OUTBOX_STATUSES.map((value) => (
              <option key={value} value={value}>
                {value.charAt(0) + value.slice(1).toLowerCase()}
              </option>
            ))}
          </SelectField>
          <Button type="submit" busy={outbox.isFetching}>
            Apply
          </Button>
        </form>
      </section>

      <section className={styles.card} aria-labelledby="outbox-heading">
        <h2 className={styles.cardTitle} id="outbox-heading">
          Outbox items
        </h2>

        {outbox.isPending && <PageSpinner label="Loading notification outbox" />}
        {outbox.isError && <Alert tone="error">{toDisplayMessage(outbox.error)}</Alert>}
        {retry.isError && <Alert tone="error">{toDisplayMessage(retry.error)}</Alert>}

        {outbox.isSuccess && outbox.data.items.length === 0 && (
          <EmptyState
            title={
              appliedStatus === undefined ? 'No outbox items' : 'No notifications match this filter'
            }
          >
            {appliedStatus === undefined
              ? 'Notification records appear here when created.'
              : 'Try another status or choose All to clear the filter.'}
          </EmptyState>
        )}

        {outbox.isSuccess && outbox.data.items.length > 0 && (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table} aria-label="Notification outbox">
                <thead>
                  <tr>
                    <th scope="col">Created</th>
                    <th scope="col">Channel</th>
                    <th scope="col">Template</th>
                    <th scope="col">Recipient</th>
                    <th scope="col">Status</th>
                    <th scope="col">Attempts</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {outbox.data.items.map((item) => (
                    <tr key={item.id}>
                      <td>{new Date(item.createdAt).toLocaleString()}</td>
                      <td>{item.channel}</td>
                      <td>
                        <code>{item.templateCode}</code>
                      </td>
                      <td>{item.recipient}</td>
                      <td>
                        <StatusBadge status={item.status} />
                      </td>
                      <td>{item.attemptCount}</td>
                      <td>
                        {can(PERMISSIONS.NOTIFICATIONS_RETRY) &&
                          item.status === 'FAILED' &&
                          !item.templateCode.includes('OTP') && (
                            <Button
                              variant="secondary"
                              busy={retry.isPending && retry.variables === item.id}
                              onClick={() => {
                                retry.mutate(item.id);
                              }}
                            >
                              Retry
                            </Button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <nav className={styles.pagination} aria-label="Pagination">
              <Button
                variant="secondary"
                disabled={outbox.isFetching || !outbox.data.pagination.hasPreviousPage}
                onClick={() => {
                  setPage((current) => current - 1);
                }}
              >
                Previous
              </Button>
              <span>
                Page {outbox.data.pagination.page} of {outbox.data.pagination.totalPages}
              </span>
              <Button
                variant="secondary"
                disabled={outbox.isFetching || !outbox.data.pagination.hasNextPage}
                onClick={() => {
                  setPage((current) => current + 1);
                }}
              >
                Next
              </Button>
            </nav>
          </>
        )}
      </section>
    </div>
  );
}
